import { useCallback, useState, useMemo } from 'react';
import { useSubscription } from './useSubscription';
import { useClerkAuth } from './useClerkAuth';

export interface GuruSubscriptionGateReturn {
  // Auth state
  isAuthLoaded: boolean;
  isSignedIn: boolean;
  
  // Subscription state
  plan: string;
  canAccessAiAnalysis: boolean;
  isSubscriptionLoading: boolean;
  isAutoSubscribing: boolean;
  
  // Gate function - returns true if user can proceed
  checkGuruAccess: () => boolean;
  
  // Modal controls
  showUpgradePrompt: boolean;
  closeUpgradePrompt: () => void;
  openFullUpgradeModal: () => void;
  
  // Full upgrade modal
  isUpgradeModalOpen: boolean;
  closeUpgradeModal: () => void;
}

export function useGuruSubscriptionGate(): GuruSubscriptionGateReturn {
  const { isAuthLoaded, isSignedIn, openSignIn } = useClerkAuth();
  const { 
    plan, 
    isLoading: isSubscriptionLoading, 
    checkFeatureAccess,
    openUpgradeModal,
    closeUpgradeModal,
    isUpgradeModalOpen,
    isAutoSubscribing,
  } = useSubscription();

  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Memoize feature access check to avoid recalculating on every render
  const canAccessAiAnalysis = useMemo(() => {
    return checkFeatureAccess('aiAnalysis');
  }, [plan, checkFeatureAccess]);

  const checkGuruAccess = useCallback((): boolean => {
    console.log('[useGuruSubscriptionGate] checkGuruAccess called', {
      plan,
      canAccessAiAnalysis,
      isAuthLoaded,
      isSignedIn,
      isSubscriptionLoading
    });

    // First check authentication
    if (!isAuthLoaded) {
      console.warn('[useGuruSubscriptionGate] Auth not loaded yet');
      return false;
    }

    // Build return URL with context
    const returnUrl = (() => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('guru', '1');
        return url.toString();
      } catch {
        return undefined;
      }
    })();

    // If not signed in, prompt sign in
    if (!isSignedIn) {
      openSignIn({ returnUrl });
      return false;
    }

    // Check subscription for AI analysis
    if (!canAccessAiAnalysis) {
      console.log('[useGuruSubscriptionGate] Setting showUpgradePrompt to TRUE');
      setShowUpgradePrompt(true);
      return false;
    }

    // All checks passed
    return true;
  }, [isAuthLoaded, isSignedIn, openSignIn, canAccessAiAnalysis, plan, isSubscriptionLoading]);

  const closeUpgradePrompt = useCallback(() => {
    setShowUpgradePrompt(false);
  }, []);

  const openFullUpgradeModal = useCallback(() => {
    setShowUpgradePrompt(false);
    openUpgradeModal();
  }, [openUpgradeModal]);

  return {
    isAuthLoaded,
    isSignedIn,
    plan,
    canAccessAiAnalysis,
    isSubscriptionLoading,
    isAutoSubscribing,
    checkGuruAccess,
    showUpgradePrompt,
    closeUpgradePrompt,
    openFullUpgradeModal,
    isUpgradeModalOpen,
    closeUpgradeModal,
  };
}
